import { Injectable } from '@angular/core';
import { ContactCompanyEntity, ContactIndividualEntity, ProjectSearchResponseDto, PropertyEntity, PublishPortalEntity } from '@app/client';
import dayjs from 'dayjs';
import { Observable, combineLatest, map, of, tap } from 'rxjs';
import * as XLSX from 'xlsx';
import { LeadStateEnum, leadStateLabels } from '../enums/lead-state.enum';
import { getOpportunitySaleStateOptions, OpportunitySaleStateEnum } from '../enums/opportunity-sale-state.enum';
import { IPaginateResponse } from '../interfaces/paginate-response.interface';
import { DomainValuesSharedService } from './domain-values.shared.service';
import { SourcesSharedService } from './sources-shared.service';
import { getOpportunityPurchaseStateOptions } from '../enums/opportunity-purchase-state.enum';

const jsonToXlsx = (jsonData: any) => {
  const wb = XLSX.utils.book_new();
  const newWs = XLSX.utils.json_to_sheet(jsonData);
  XLSX.utils.book_append_sheet(wb, newWs);
  const rawExcel = XLSX.write(wb, { type: 'array', bookType: 'xlsx' });
  return rawExcel;
};
const aoaToXlsx = (data: any) => {
  const wb = XLSX.utils.book_new();
  const newWs = XLSX.utils.aoa_to_sheet(data);
  XLSX.utils.book_append_sheet(wb, newWs);
  const rawExcel = XLSX.write(wb, { type: 'array', bookType: 'xlsx' });
  return rawExcel;
};
@Injectable()
export class CsvUtilsService {
  sourceList: any[] = [];
  domainValues: any[] = [];

  constructor(private sourcesServices: SourcesSharedService, private domainValuesService: DomainValuesSharedService) {
    combineLatest([this.sourcesServices.getSourceOptions(), this.domainValuesService.getDomainValues()]).subscribe((values) => {
      this.sourceList = values[0];
      this.domainValues = values[1];
    });
  }
  mapCsv<T = any>(listService: Observable<IPaginateResponse<T>>, mapKey: string) {
    return listService.pipe(
      map((res) => res.data),
      map((values) => values.map((v) => this.csvFactory(v, mapKey))),
      tap((mappedValues) => this.downloadFile(mappedValues, mapKey)),
    );
  }
  arrayToCsv<T = any>(list$: Observable<T[]>, mapKey: string) {
    return list$.pipe(
      map((values) => values.map((v) => this.csvFactory(v, mapKey))),
      tap((mappedValues) => this.downloadFile(mappedValues, mapKey)),
    );
  }

  aoaCsv<T = any>(service: Observable<T>, mapKey: string) {
    return service.pipe(
      map((value) => this.csvFactory(value, mapKey)),
      tap((mappedValues) => this.downloadFile(mappedValues, mapKey)),
    );
  }
  aoaCsvFromObj<T = any>(obj: T, mapKey: string) {
    return of(obj).pipe(
      map((value) => this.csvFactory(value, mapKey)),
      tap((mappedValues) => this.downloadFile(mappedValues, mapKey)),
    );
  }

  private downloadFile(data: any, mapKey?: string) {
    const replacer = (value: any) => (value === null || value === undefined ? '' : value);
    const header = Object.keys(data[0]);
    const csv = data.map((row: any) => header.map((fieldName: any) => JSON.stringify(row[fieldName], replacer)).join(','));
    csv.unshift(header.join(','));
    const a = document.createElement('a');
    const blobData = mapKey === 'properties' ? aoaToXlsx(data) : jsonToXlsx(data);
    const blob = new Blob([blobData], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,',
    });
    const url = window.URL.createObjectURL(blob);
    a.href = url;
    a.download = mapKey ? mapKey + '.xlsx' : dayjs().format('DD/MM/YYYY') + '.xlsx';
    a.click();
    window.URL.revokeObjectURL(url);
    a.remove();
  }
  private sourceType(value: number) {
    if (!this.sourceList || this.sourceList.length === 0) {
      return '-';
    }
    const source = this.sourceList.find((s) => s.value == value);
    return source ? source.type : '-';
  }
  private sourceCampaign(value: number) {
    if (!this.sourceList || this.sourceList.length === 0) {
      return '-';
    }
    const source = this.sourceList.find((s) => s.value == value);
    return source ? source.campaign : '-';
  }
  private sourceLabel(value: number) {
    if (!this.sourceList || this.sourceList.length === 0) {
      return '-';
    }
    const source = this.sourceList.find((s) => s.value == value);
    return source ? source.label : '-';
  }
  private domainValue(value: number, domainValueType: string) {
    if (!this.domainValues || this.domainValues.length === 0) {
      return '-';
    }
    const dValues = this.domainValues.filter((v) => v.type === domainValueType);
    const found = dValues.find((v) => v.code == value);
    return found ? found.label : '-';
  }
  private getLeadLabel(value: LeadStateEnum, labelType: keyof typeof leadStateLabels) {
    const states = leadStateLabels[labelType];
    const elem = states.find((d) => d.value == value);
    return elem && elem.label;
  }
  private getOppLabel(
    value: OpportunitySaleStateEnum,
    opportunityStateLabels: {
      label: string;
      value: number;
    }[],
  ) {
    const states = opportunityStateLabels;
    const elem = states.find((d) => d.value == value);
    return elem && elem.label;
  }

  private csvFactory(row: any, mapKey: string) {
    if (mapKey === 'project_purchase') {
      return this.mapProjectPurchase(row);
    } else if (mapKey === 'project_sale') {
      return this.mapProjectSale(row);
    } else if (mapKey === 'opportunity_purchase') {
      return this.mapOpportunityPurchase(row);
    } else if (mapKey === 'opportunity_sale') {
      return this.mapOpportunitySale(row);
    } else if (mapKey === 'event') {
      return this.mapEvents(row);
    } else if (mapKey === 'properties') {
      return this.transformRowToColumns(row);
    } else if (mapKey === 'property_list') {
      return this.mapProperties(row);
    } else if (mapKey === 'users_list') {
      return this.mapUsers(row);
    }

    return row;
  }

  mapUsers(row: any) {
    return {
      nome: row?.firstName,
      cognomr: row?.lastName,
      email: row?.email,
      telefono: row?.mobilePhone,
      ruoli: row?.roles,
      attivo: row?.active,
      'Ultimo Login': row?.lastLoginInstant,
      'Nome Sede': row?.site?.name,
      'Provincia Sede': row?.site?.address?.province,
      Area: row?.site?.area,
      'BC Id': row?.bcId,
      'Codice fiscale': row?.fiscalCode,
      Abilitato: row?.isQualified,
      Fittizio: row?.isDummy,
      'Delta appuntamenti': row?.eventsCountDelta,
      'Limite mensile': row?.monthLimit,
    };
  }

  mapProperties(row: any) {
    return {
      codice: row.data?.propertyObj?.name,
      tipologia_immobile: this.domainValue(row.data?.propertyObj?.data?.propertyTypology, 'property_typology'),
      agente: row.data?.agentObj?.userFullname,
      data_incarico: dayjs(row?.data?.opportunityInsertDate).format('DD/MM/YYYY HH:mm'),
      data_scadenza_incarico: dayjs(row?.data.assignment?.expiryAt).format('DD/MM/YYYY'),
      pubblicato: this.mapPubblicationsToString(row?.data.publish),
      nome_proprietario: `${row.data?.contactObj?.firstName || row.data?.contactObj?.businessName || ''} ${
        row.data?.contactObj?.lastName || ''
      }`,
      stato: this.domainValue(row.data?.propertyObj?.data?.propertyState, 'property_state'),
      indirizzo: row.data?.propertyObj?.data?.address?.formatted_address,
      provincia: row.data?.propertyObj?.data?.address?.province,
      superficie: row.data?.finalCalculatedPropertyMq,
      numero_di_locali: row?.data?.propertyObj?.data?.totalRooms,
      prezzo_valutazione: row.data?.finalCalculatedPropertyPrice,
      mq_prezzo_valutazione: row.data?.finalCalculatedPropertyPriceForMq,
      prezzo_pubblicita: row?.data.propertyDeviationPrice?.sellingPrice,
      mq_prezzo_pubblicita: row?.data.propertyDeviationPrice?.mqSellingPrice,
      scostamento_economico: row?.data.propertyDeviationPrice?.deviation,
      scostamento_percentuale: row?.data.propertyDeviationPrice?.deviationPerc,
      provvigione: row?.data.assignment?.calculatedCommission,
      giorni_in_vendita: row?.data.daysForSale,
      totale_appuntamenti_di_visione: row.data?.visitEventsCount,
      openhouse: row.data?.openHouseEvents && row.data?.openHouseEvents?.length > 0 ? 'Si' : 'No',
    };
  }

  mapEvents(row: any) {
    return {
      nome: row?.contactObj?.firstName || row.contactObj?.businessName,
      cognome: row?.contactObj?.lastName,
      telefono: row?.contactObj?.phones ? row.contactObj?.phones[0]?.phoneNumber : '',
      email: row?.contactObj?.emails ? row.contactObj?.emails[0]?.email : '',
      indirizzo: row?.address?.formatted_address,
      provincia: row?.address?.province,
      data_inserimento: dayjs(row?.createdAt).format('DD/MM/YYYY HH:mm'),
      data_inizio: dayjs(row?.start).format('DD/MM/YYYY HH:mm'),
      data_fine: dayjs(row?.end).format('DD/MM/YYYY HH:mm'),
      utente: row?.userObj?.firstName + ' ' + row?.userObj?.lastName,
      creato_da: row?.insertUserObj?.firstName + ' ' + row?.insertUserObj?.lastName,
      tipo: this.domainValue(row?.type, 'event_type'),
      stato: this.domainValue(row?.state, 'event_state') || 'Non Esistato',
      fonte_tipo: this.domainValue(this.sourceType(row?.source), 'source_lead_type'),
      fonte: this.sourceLabel(row?.source),
      campagna: this.sourceCampaign(row?.source),
      utm_campaign: row?.tracking?.utmCampaign || row?.tracking?.utmCampaign,
      utm_source: row?.tracking?.utmSource || row?.tracking?.utmSource,
      data_creazione_opp: dayjs(row?.opportunityInsertDate).format('DD/MM/YYYY HH:mm'),
    };
  }

  mapOpportunitySale(row: ProjectSearchResponseDto) {
    return {
      nome: (<ContactIndividualEntity>row.data?.contactObj)?.firstName || (<ContactCompanyEntity>row.data?.contactObj)?.businessName,
      cognome: (<ContactIndividualEntity>row.data?.contactObj)?.lastName,
      telefono: row.data?.contactObj?.phones ? row.data?.contactObj?.phones[0]?.phoneNumber : '',
      email: row.data?.contactObj?.emails ? row.data?.contactObj?.emails[0]?.email : '',
      nome_operatore: row.data?.operatorObj?.userFullname,
      creato_da: `${row.data?.opportunityCreatorObj?.userFullname || ''}`,
      nome_agente: row.data?.agentObj?.userFullname,
      property: row.data?.propertyObj?.name,
      data_di_creazione: dayjs(row.data?.opportunityInsertDate).format('DD/MM/YYYY HH:mm'),
      data_di_inserimento: dayjs(row.data?.insertDate).format('DD/MM/YYYY HH:mm'),
      indirizzo: row.data?.propertyObj?.data?.address?.formatted_address,
      provincia: row.data?.propertyObj?.data?.address?.province,
      fonte_tipo: this.domainValue(this.sourceType(row.data?.source), 'source_lead_type'),
      fonte: this.sourceLabel(row.data?.source),
      campagna: this.sourceCampaign(row.data?.source),
      utm_campaign: row.data?.tracking?.utmCampaign,
      utm_source: row.data?.tracking?.utmSource,
      stato: this.getOppLabel(row.status?.opportunity as any, getOpportunitySaleStateOptions(row.data.isRenting)),
      data_prossima_attivita: dayjs(row.data?.nextEventDate).format('DD/MM/YYYY HH:mm'),
      note: row.data?.nextEventNote,
      provvigione_totale: row?.data?.commissions?.finalizedTotalCommission,
      data_sopralluogo_fissato: row?.data?.firstInspectionDate ? dayjs(row?.data?.firstInspectionDate).format('DD/MM/YYYY HH:mm') : '',
      data_sopralluogo_eseguito: row?.data?.inspectionExecutedAt ? dayjs(row?.data?.inspectionExecutedAt).format('DD/MM/YYYY HH:mm') : '',
      sede: row.data?.agentObj?.siteName,
      area: row.data?.agentObj?.siteArea,
      storeManager: row.data?.agentObj?.storeManagerFullname,
    };
  }

  mapOpportunityPurchase(row: any) {
    return {
      nome: row.data?.contactObj?.firstName || row.data?.contactObj?.businessName,
      cognome: row.data?.contactObj?.lastName,
      telefono: row.data?.contactObj?.phones ? row.data?.contactObj?.phones[0]?.phoneNumber : '',
      email: row.data?.contactObj?.emails ? row.data?.contactObj?.emails[0]?.email : '',
      data_di_creazione: dayjs(row.data?.opportunityInsertDate).format('DD/MM/YYYY HH:mm'),
      data_di_inserimento: dayjs(row.data?.insertDate).format('DD/MM/YYYY HH:mm'),
      nome_operatore: row.data?.operatorObj?.userFullname + ' ' + row.data?.operatorObj?.lastName,
      creato_da: `${row.data?.opportunityCreatorObj?.userFullname || ''} ${row.data?.opportunityCreatorObj?.lastName || ''}`,
      indirizzo: row.data?.propertyPreference?.addressZone?.formatted_address,
      provincia: row.data?.propertyPreference?.addressZone?.province,
      indirizzo_immobile: row.data?.propertyObj?.data?.address?.formatted_address ?? '',
      provincia_immobile: row.data?.propertyObj?.data?.address?.province ?? '',
      fonte_tipo: this.domainValue(this.sourceType(row.data?.source), 'source_lead_type'),
      fonte: this.sourceLabel(row.data?.source),
      campagna: this.sourceCampaign(row.data?.source),
      utm_campaign: row.data?.tracking?.utmCampaign,
      utm_source: row.data?.tracking?.utmSource,
      stato: this.getOppLabel(row.status?.opportunity, getOpportunityPurchaseStateOptions(row.data.isRenting)),
      data_prossima_attivita: dayjs(row.data?.nextEventDate).format('DD/MM/YYYY HH:mm'),
      note: row.data?.notes?.[0] ?? '',
      data_prima_visita: dayjs(row.data?.firstVisitDate).format('DD/MM/YYYY HH:mm'),
      diretto_superiore: row.data?.operatorObj?.parentUserFullname,
      sede_riferimento: row.data?.operatorObj?.siteName,
      teamManager: row.data?.operatorObj?.storeManagerFullname || row.data?.opportunityCreatorObj?.storeManagerFullname,
    };
  }

  mapProjectPurchase(row: any) {
    return {
      nome: row.data?.contactObj?.firstName || row.data?.contactObj?.businessName,
      cognome: row.data?.contactObj?.lastName,
      data_di_creazione: dayjs(row.data?.insertDate).format('DD/MM/YYYY HH:mm'),
      operatore: row.data?.operatorObj?.userFullname,
      telefono: row.data?.contactObj?.phones ? row.data?.contactObj?.phones[0]?.phoneNumber : '',
      email: row.data?.contactObj?.emails ? row.data?.contactObj?.emails[0]?.email : '',
      indirizzo: row.data?.propertyPreference?.addressZone?.formatted_address ?? row.data?.zones?.[0]?.address?.formatted_address ?? '',
      provincia: row.data?.propertyPreference?.addressZone?.province ?? row.data?.zones?.[0]?.address?.province ?? '',
      fonte_tipo: this.domainValue(this.sourceType(row.data?.source), 'source_lead_type'),
      fonte: this.sourceLabel(row.data?.source),
      campagna: this.sourceCampaign(row.data?.source),
      utm_campaign: row.data?.tracking?.utmCampaign,
      utm_source: row.data?.tracking?.utmSource,
      stato: this.getLeadLabel(row.status?.lead, 'acquisto'),
      data_prossima_attivita: dayjs(row.data?.nextEventDate).format('DD/MM/YYYY HH:mm'),
      note: row.data?.notes?.[0] ?? '',
      data_prima_visita: dayjs(row.data?.firstVisitDate).format('DD/MM/YYYY HH:mm'),
      diretto_superiore: row.data?.operatorObj?.parentUserFullname,
      sede_riferimento: row.data?.operatorObj?.siteName,
      teamManager: row.data?.operatorObj?.storeManagerFullname || row.data?.opportunityCreatorObj?.storeManagerFullname,
    };
  }

  mapProjectSale(row: any) {
    return {
      nome: row?.data?.contactObj?.firstName || row?.data?.contactObj?.businessName,
      cognome: row?.data?.contactObj?.lastName,
      data_di_creazione: dayjs(row?.createdAt).format('DD/MM/YYYY HH:mm'),
      data_di_inserimento: dayjs(row?.data?.insertDate).format('DD/MM/YYYY HH:mm'),
      operatore: row?.data?.operatorObj?.userFullname,
      utente_assegnato: row?.data.agentObj?.userFullname,
      telefono: row?.data?.contactObj?.phones ? row?.data?.contactObj?.phones[0]?.phoneNumber : '',
      email: row?.data?.contactObj?.emails ? row?.data?.contactObj?.emails[0]?.email : '',
      indirizzo_residenza: (row.data?.contactObj as ContactIndividualEntity)?.address?.formatted_address,
      provincia_residenza: (row.data?.contactObj as ContactIndividualEntity)?.address?.province,
      indirizzo_immobile: row?.data?.propertyObj?.data?.address?.formatted_address,
      provincia_immobile: row?.data?.propertyObj?.data?.address?.province,
      fonte_tipo: row && row?.data?.source ? this.domainValue(this.sourceType(row?.data?.source), 'source_lead_type') : null,
      fonte: this.sourceLabel(row?.data?.source),
      campagna: this.sourceCampaign(row?.data?.source),
      utm_campaign: row?.data?.tracking?.utmCampaign,
      utm_source: row?.data?.tracking?.utmSource,
      stato: this.getLeadLabel(row?.status?.lead, 'vendita'),
      data_prossima_attivita: dayjs(row?.data?.nextEventDate).format('DD/MM/YYYY HH:mm'),
      note: row?.data?.nextEventNote,
      teamManager: row?.data?.agentObj?.storeManagerFullname || row?.data?.operatorObj?.storeManagerFullname,
    };
  }

  private mapProperty(property: PropertyEntity) {
    return {
      'Identificativo immobile': property?.name,
      Indirizzo: property?.data?.address?.formatted_address,
      Tipologia: this.domainValue(property?.data?.propertyTypology as any, 'property_typology'),
      'Tipo propietà': this.domainValue(property?.data?.propertyType as any, 'property_type'),
      'Prezzo di vendidta': property?.data?.sellingPrice,
      'Prezzo su richiesta': property?.data?.priceOnRequest,
      'Spese condominiali': property?.data?.condominiumFees,
      'Nessuna spesa condominiale': property?.data?.hasCondominiumFees,
      'Spese riscaldamento (€/anno)': property?.data?.heatingCosts,
      'Nessuna spesa riscaldamento': property?.data?.hasHeatingCosts,
      'Categoria catastale': property?.data?.cadastralCategory,
      'Rendita catastale': property?.data?.cadastralIncome,
      Libero: property?.data?.isFree,
      'Affitto con Riscatto': property?.data?.rentWithRedemption,
      'A reddito': property?.data?.toIncome,
      Mq: property?.data?.squareMeters,
      Budget: property?.data?.budget,
      'Progetto di vendita': property?.data?.saleProject,
      'Motivo della vendita': property?.data?.saleReason,
      'Camere da letto': property?.data?.bedroomsNumber,
      'Altre stanze': property?.data?.otherRooms,
      'Totale locali': property?.data?.totalRooms,
      Cucina: this.domainValue(property?.data?.kitchen as any, 'kitchen'),
      'Valore Cucina': this.domainValue(property?.data?.kitchenQuality as any, 'kitchen_quality'),
      Arredamento: this.domainValue(property?.data?.furniture as any, 'furniture'),
      Bagni: property?.data?.bathroomsNumber,
      Giardino: this.domainValue(property?.data?.garden as any, 'garden'),
      'Mq giardino': property?.data?.gardenSquareMeters,
      Garage: this.domainValue(property?.data?.garage as any, 'garage'),
      'Mq garage': property?.data?.garageSquareMeters,
      'Posti auto': property?.data?.boxNumber,
      Balcone: property?.data?.balcony,
      'Mq balcone': property?.data?.balconySquareMeters,
      Terrazzo: property?.data?.terrace,
      'Armadi a muro': property?.data?.builtInWardrobes,
      Cantina: property?.data?.cellar,
      Mansarda: property?.data?.attic,
      Taverna: property?.data?.tavern,
      'Infissi esterni': this.domainValue(property?.data?.externalFixtures as any, 'external_fixtures'),
      'Impianto TV': this.domainValue(property?.data?.tvSystem as any, 'tv_system'),
      'Servizio portineria': this.domainValue(property?.data?.conciergeService as any, 'concierge_service'),
      'Porta blindata': property?.data?.securityDoor,
      Allarme: property?.data?.alarm,
      'Cancello elettrico': property?.data?.electricGate,
      Videocitofono: property?.data?.videoIntercom,
      'Fibra ottica': property?.data?.opticFiber,
      Camino: property?.data?.chimney,
      Idromassaggio: property?.data?.hydromassage,
      Piscina: property?.data?.pool,
      'Campo da tennis': property?.data?.tennisCourt,
      'Anno di costruzione': property?.data?.buildYear,
      'Classe immobile': this.domainValue(property?.data?.propertyClass as any, 'property_class'),
      Stato: this.domainValue(property?.data?.propertyState as any, 'property_state'),
      Piano: this.domainValue(property?.data.floor as any, 'floor'),
      'Su più livelli': property?.data?.onSeveralLevels,
      'Totale piani edificio': property?.data?.totalBuildingFloors,
      Acensore: property?.data?.lift,
      'Esigenze disabili': property?.data?.disabledNeeds,
      'Lati liberi': this.domainValue(property?.data?.freeSides as any, 'free_sides'),
      Affaccio: this.domainValue(property?.data?.overlooking as any, 'overlooking'),
      Riscaldamento: this.domainValue(property.data?.heating as any, 'heating'),
      'Tipo impianto riscaldamento': this.domainValue(property?.data?.heatingPlantType as any, 'heating_plant_type'),
      'Alimentazione riscaldamento': this.domainValue(property?.data?.supply as any, 'heating_type'),
      'Impianto climatizzazione': this.domainValue(property?.data?.airConditioning as any, 'air_conditioning_plant'),
      'Classe energetica': this.domainValue(property?.data?.energyClass as any, 'energy_class'),
      /* 'Area occupata': property?.data?.coveredArea, */
      'Qualità finiture': this.domainValue(property?.data?.finishesQuality as any, 'finishes'),
      'Valore impianti': this.domainValue(property?.data?.plantQuality as any, 'plants_quality'),
      Descrizione: property?.data?.description,
    };
  }
  private transformRowToColumns(row: any) {
    const mapped: any = this.mapProperty(row);
    const keys = Object.keys(mapped);
    const aoa = [];
    while (keys.length > 0) {
      const threeColumn = [];
      threeColumn.push(keys[0], mapped[keys[0]]);
      keys.shift();
      threeColumn.push(keys[0], mapped[keys[0]]);
      keys.shift();
      threeColumn.push(keys[0], mapped[keys[0]]);
      keys.shift();
      aoa.push(threeColumn);
    }
    return [['Label', 'Valore', 'Label', 'Valore', 'Label', 'Valore'], ...aoa];
  }

  private mapPubblicationsToString(publish: PublishPortalEntity): string {
    const pubblications = [];
    if (publish) {
      if (publish?.fimm?.extId) {
        pubblications.push('Portale');
      }
      if (publish?.immobiliareIt?.extId) {
        pubblications.push('Immobiliare.it');
      }
      if (publish?.idealista?.extId) {
        pubblications.push('Idealista');
      }
    }
    return pubblications.join(',');
  }
}